/**
 * Provides a summary of values in a given group of assets in the inventory table.
 * Conditions account for include:
 *  - a completely new group
 *  - a group all with the same value
 *  - a group with mixed values, including optionally new values
 */
import React from 'react';
import { Box, Flex, Tag, Text, Tooltip } from 'core/components';
import AssetTagInventoryModel from 'app/stores/assetTags/AssetTagInventoryModel';

interface Props {
  group: AssetTagInventoryModel[];
}

function valueRenderer({ group }: Props) {
  const uniqueValues = Array.from(new Set((group || []).map((item) => item.get('value'))));
  const hasNew = uniqueValues.includes('');
  const uniqueDefinedValues = uniqueValues.filter((value) => value !== '');
  const singleValue = uniqueDefinedValues.length === 1 ? uniqueDefinedValues[0] : undefined;

  if (hasNew && uniqueValues.length === 1) {
    // if the group is completely new, show a simple tag
    return <Tag intent="primary">New</Tag>;
  }

  // otherwise indicate that there are mixed values with a special decorated tag if there are new values in the group
  return (
    <Tooltip
      placement="top"
      content={
        <Box maxWidth={150}>
          <Text>{uniqueDefinedValues.join(', ')}</Text>
          {hasNew && <Text as="div">(includes new values)</Text>}
        </Box>
      }
      disabled={!!singleValue}
    >
      <Flex alignItems="center" gap={1}>
        <Text>{singleValue ?? 'Mixed Values'}</Text>
        {uniqueDefinedValues.length > 1 && (
          <Tag intent={hasNew ? 'primary' : 'none'} minimal round>
            {uniqueDefinedValues.length}
          </Tag>
        )}
      </Flex>
    </Tooltip>
  );
}

export default valueRenderer;
